import { createSlice } from "@reduxjs/toolkit";
import { fetchFavorites, addFavorite, removeFavorite } from "./favoritesApi";

const initialState = {
  // Topic favorites
  topicMap: {}, // { [topicId]: favoriteDocId }
  topicFavorites: [], // full topic favorite items [{ id, itemId, name, picture, ... }]
  topicLoading: false,

  // Coach favorites
  coachMap: {}, // { [coachId]: favoriteDocId }
  coachFavorites: [], // full coach favorite items [{ id, itemId, name, picture, ... }]
  coachLoading: false,

  // Pending optimistic state for add/remove
  _pendingAdd: {}, // { [itemId]: "pending" }
  _revertData: null, // stores data to revert on remove failure
};

export const favoritesSlice = createSlice({
  name: "favorites",
  initialState,
  reducers: {
    clearFavorites: () => initialState,
  },
  extraReducers: (builder) => {
    // ---- Fetch favorites ----
    builder.addCase(fetchFavorites.pending, (state, action) => {
      const type = action.meta.arg || "TOPIC";
      if (type === "TOPIC") {
        state.topicLoading = true;
      } else {
        state.coachLoading = true;
      }
    });
    builder.addCase(fetchFavorites.fulfilled, (state, action) => {
      const { type, data } = action.payload;
      const map = {};
      const items = data || [];
      items.forEach((item) => {
        map[item.itemId] = item.id;
      });
      if (type === "TOPIC") {
        state.topicMap = map;
        state.topicFavorites = items;
        state.topicLoading = false;
      } else {
        state.coachMap = map;
        state.coachFavorites = items;
        state.coachLoading = false;
      }
    });
    builder.addCase(fetchFavorites.rejected, (state, action) => {
      const type = action.meta.arg || "TOPIC";
      if (type === "TOPIC") {
        state.topicLoading = false;
      } else {
        state.coachLoading = false;
      }
    });

    // ---- Add favorite — optimistic ----
    builder.addCase(addFavorite.pending, (state, action) => {
      const itemId = action.meta.arg;
      state._pendingAdd[itemId] = "pending";
    });
    builder.addCase(addFavorite.fulfilled, (state, action) => {
      const { itemId, favoriteDocId, type } = action.payload;
      delete state._pendingAdd[itemId];
      if (type === "TOPIC") {
        state.topicMap[itemId] = favoriteDocId;
      } else {
        state.coachMap[itemId] = favoriteDocId;
      }
    });
    builder.addCase(addFavorite.rejected, (state, action) => {
      const itemId = action.meta.arg;
      delete state._pendingAdd[itemId];
    });

    // ---- Remove favorite — optimistic ----
    builder.addCase(removeFavorite.pending, (state, action) => {
      const { favoriteDocId, itemId, type } = action.meta.arg;
      if (type === "TOPIC") {
        if (state.topicMap[itemId]) {
          state._revertData = { itemId, favoriteDocId, type };
          delete state.topicMap[itemId];
        }
      } else {
        if (state.coachMap[itemId]) {
          state._revertData = { itemId, favoriteDocId, type };
          delete state.coachMap[itemId];
        }
      }
    });
    builder.addCase(removeFavorite.fulfilled, (state) => {
      state._revertData = null;
    });
    builder.addCase(removeFavorite.rejected, (state, action) => {
      if (state._revertData) {
        const { itemId, favoriteDocId, type } = state._revertData;
        if (type === "TOPIC") {
          state.topicMap[itemId] = favoriteDocId;
        } else {
          state.coachMap[itemId] = favoriteDocId;
        }
        state._revertData = null;
      }
    });
  },
});

export const { clearFavorites } = favoritesSlice.actions;
export default favoritesSlice.reducer;
